export const formatDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateKey = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (dateKey, delta) => {
  const date = parseDateKey(dateKey);
  date.setDate(date.getDate() + delta);
  return formatDateKey(date);
};

export const addMonths = (dateKey, delta) => {
  const date = parseDateKey(dateKey);
  date.setMonth(date.getMonth() + delta);
  return formatDateKey(date);
};

// Monday-start week containing the given date key
export const startOfWeek = (dateKey) => {
  const date = parseDateKey(dateKey);
  const day = date.getDay(); // 0 Sun … 6 Sat
  const offset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + offset);
  return formatDateKey(date);
};

export const startOfMonth = (dateKey) => {
  const date = parseDateKey(dateKey);
  date.setDate(1);
  return formatDateKey(date);
};

export const endOfMonth = (dateKey) => {
  const date = parseDateKey(dateKey);
  date.setMonth(date.getMonth() + 1, 0);
  return formatDateKey(date);
};

export const eachDay = (fromKey, toKeyInclusive) => {
  const days = [];
  let cursor = fromKey;
  while (cursor <= toKeyInclusive) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
};

export const weekDays = (dateKey) => {
  const start = startOfWeek(dateKey);
  return eachDay(start, addDays(start, 6));
};

// Month grid padded to full weeks (Mon–Sun), for calendar cells
export const monthGridDays = (dateKey) => {
  const monthStart = startOfMonth(dateKey);
  const monthEnd = endOfMonth(dateKey);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = addDays(startOfWeek(monthEnd), 6);
  return eachDay(gridStart, gridEnd);
};

// Match backend slotStartMs: treat wall-clock HH:MM on a date key as UTC.
export const slotStartMs = (dateKey, time) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const [hours, minutes] = String(time).split(':').map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes);
};

export const rangeBoundsMs = (fromKey, toKeyInclusive) => ({
  from: slotStartMs(fromKey, '00:00'),
  to: slotStartMs(toKeyInclusive, '23:59') + 60 * 1000,
});

export const formatDisplayDate = (dateKey) =>
  parseDateKey(dateKey).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

export const formatShortWeekday = (dateKey) =>
  parseDateKey(dateKey).toLocaleDateString(undefined, { weekday: 'short' });

export const formatDayNumber = (dateKey) => parseDateKey(dateKey).getDate();

export const formatMonthLabel = (dateKey) =>
  parseDateKey(dateKey).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

export const formatWeekLabel = (dateKey) => {
  const days = weekDays(dateKey);
  const start = parseDateKey(days[0]);
  const end = parseDateKey(days[6]);
  const sameMonth = start.getMonth() === end.getMonth();
  const startLabel = start.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
  const endLabel = end.toLocaleDateString(undefined, {
    day: 'numeric',
    month: sameMonth ? undefined : 'short',
    year: 'numeric',
  });
  return `${startLabel} – ${endLabel}`;
};

export const formatTime = (ms) => {
  const date = new Date(ms);
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const formatDateKeyFromMs = (ms) => {
  const date = new Date(ms);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export { formatMoneyByCurrency as formatCurrency } from '../../localization/locales';

export const formatDuration = (minutes) => {
  const value = Number(minutes) || 0;
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};
